import { DataTransfer } from '@ibm-aspera/connect-sdk-js/src/core/types';
import { Connect, ConnectInstaller } from '@ibm-aspera/connect-sdk-js';
import { useEffect, useMemo, useRef } from 'react';
import type { Connector } from '@/redux/slices/slice_types';
import { selectConnectors, selectCurrentPathString, selectHomePathId } from '@/redux/selectors/fileManagerSelectors';
import axios from '../../_utils/axios';
import { useToast } from '../_common/Toast';
import { useAppSelector } from '@/redux/hooks';

const connectSpec = {
  allow_dialogs: false,
  return_paths: true,
  return_files: false,
  use_absolute_destination_path: false
};

const useAspera = () => {
  const { error } = useToast();
  const currentPath = useAppSelector(selectCurrentPathString);
  const currentPathId = useAppSelector(selectHomePathId);
  const connectors: Record<string, Connector> = useAppSelector(selectConnectors);
  const connectClientRef = useRef(new Connect());
  const installerRef = useRef(new ConnectInstaller());
  const statusInterval = useRef<ReturnType<typeof setInterval>>();
  const hasInitializedRef = useRef(false);
  const hasAsperaNode = useMemo(() => {
    const values = Object.values(connectors);
    return values.length > 0 && values.some((connector) => connector?.aspera_node?.allows_upload === true);
  }, [connectors]);

  const uploadFile = (data: DataTransfer) => {
    const { files } = data.dataTransfer;
    const fileToUpload = files[0].name;
    axios
      .post(`/vfs/file_sources/${currentPathId}/upload/create/`, {
        filename: files[0].name
      })
      .then((res) => {
        const transfer_spec = res?.data?.data?.config?.transfer_specs?.[0]?.transfer_spec;
        if (!transfer_spec) return console.warn('No transfer spec found in response');
        transfer_spec.paths = [{ source: fileToUpload }];
        transfer_spec.destination_root = currentPath.length ? `${currentPath}/` : '/';
        connectClientRef.current.startTransfer(transfer_spec, connectSpec, (cb) => console.log(cb));
      })
      .catch((err) => {
        console.log(err);
        error(err.response?.data?.message || 'There was an error uploading the file(s).');
      });
  };

  const upload = () => {
    const options = {
      allowMultipleSelection: false
    };

    connectClientRef.current
      .showSelectFileDialogPromise(options)
      .then(uploadFile)
      .catch(() => {
        // error('Unable to select files');
      });
  };

  useEffect(() => {
    if (hasAsperaNode && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      connectClientRef.current.initSession();
      statusInterval.current = setInterval(() => {
        const status = connectClientRef.current.getStatus();
        if (status !== 'INITIALIZING') {
          clearInterval(statusInterval.current);
          if (status === 'FAILED') {
            installerRef.current.showDownload();
          }
        }
      }, 1000);
    }

    return () => clearInterval(statusInterval.current);
  }, [hasAsperaNode]);

  return {
    upload
  };
};

export default useAspera;
